import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { isCompanyAdmin } from '../utils/planAccess';
import { fetchCompanyUsers, formatUserLabel } from '../utils/companyUsers';
import { formatTaskStatus, formatTaskPriority } from '../utils/taskLabels';
import { formatDateShort } from '../utils/locale';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import './Analytics.css';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';

const CHART_COLORS = ['#D052F2', '#382059', '#7B68EE', '#48BB78', '#ED8936', '#4299E1'];

const toDateInput = (date) => date.toISOString().slice(0, 10);

const getPresetRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { startDate: toDateInput(start), endDate: toDateInput(end) };
};

const useIsNarrowViewport = (maxWidth = 640) => {
  const query = `(max-width: ${maxWidth}px)`;
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(query);
    const handleChange = (event) => setIsNarrow(event.matches);
    setIsNarrow(mq.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [query]);

  return isNarrow;
};

const AnalyticsChartEmpty = ({ title, description }) => (
  <EmptyState
    icon="bar_chart"
    title={title}
    description={description}
  />
);

const Analytics = () => {
  const { t, i18n } = useTranslation(['analytics', 'common']);
  const { user } = useContext(AuthContext);
  const isAdmin = isCompanyAdmin(user);
  const companyId = user?.active_company_id || user?.company_id;
  const isNarrow = useIsNarrowViewport();

  const chartHeight = isNarrow ? 210 : 280;
  const chartHeightWide = isNarrow ? 220 : 300;
  const tickFont = isNarrow ? 10 : 12;
  const tickFontSmall = isNarrow ? 10 : 11;
  const categoryAxisWidth = isNarrow ? 70 : 90;
  const pieOuterRadius = isNarrow ? 68 : 90;
  const legendProps = isNarrow
    ? { wrapperStyle: { fontSize: 11, paddingTop: 4 }, iconSize: 8 }
    : {};
  const mutedTick = { fill: 'var(--color-text-muted)', fontSize: tickFont };
  const mutedTickSmall = { fill: 'var(--color-text-muted)', fontSize: tickFontSmall };

  const presetRanges = useMemo(() => [
    { id: '7', label: t('presetLast7Days'), days: 7 },
    { id: '30', label: t('presetLast30Days'), days: 30 },
    { id: '90', label: t('presetLast90Days'), days: 90 }
  ], [t]);

  const formatMinutes = useCallback((minutes) => {
    if (minutes == null) return t('emptyValue');
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return t('durationHoursMinutes', { hours, minutes: mins });
    }
    return t('durationMinutesOnly', { minutes: mins });
  }, [t]);

  const formatPercent = useCallback((value) => {
    if (value == null) return t('emptyValue');
    return `${Math.round(value * 100)}%`;
  }, [t]);

  const formatPeriodLabel = useCallback((value) => {
    if (!value) return '';
    return formatDateShort(value, i18n.language);
  }, [i18n.language]);

  const defaultRange = useMemo(() => getPresetRange(30), []);

  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [preset, setPreset] = useState('30');
  const [bucket, setBucket] = useState('day');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterLocationId, setFilterLocationId] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [locations, setLocations] = useState([]);

  const [summary, setSummary] = useState(null);
  const [tasksTrend, setTasksTrend] = useState(null);
  const [userPerformance, setUserPerformance] = useState(null);
  const [timeSpent, setTimeSpent] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin || !companyId) {
      return;
    }

    const loadFilters = async () => {
      try {
        const [users, locationsResponse] = await Promise.all([
          fetchCompanyUsers(companyId, user),
          axios.get('/api/locations')
        ]);
        setCompanyUsers(users);
        setLocations(locationsResponse.data.data || []);
      } catch (loadError) {
        console.error('Error loading analytics filters:', loadError);
      }
    };

    loadFilters();
  }, [isAdmin, companyId, user]);

  const queryParams = useMemo(() => {
    const params = { startDate, endDate, bucket };
    if (filterUserId) params.userId = filterUserId;
    if (filterLocationId) params.locationId = filterLocationId;
    if (filterPriority) params.priority = filterPriority;
    return params;
  }, [startDate, endDate, bucket, filterUserId, filterLocationId, filterPriority]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [summaryRes, trendRes, perfRes, timeRes] = await Promise.all([
        axios.get('/api/analytics/summary', { params: queryParams }),
        axios.get('/api/analytics/tasks-trend', { params: queryParams }),
        axios.get('/api/analytics/user-performance', { params: queryParams }),
        axios.get('/api/analytics/time-spent', { params: queryParams })
      ]);

      setSummary(summaryRes.data.data);
      setTasksTrend(trendRes.data.data);
      setUserPerformance(perfRes.data.data);
      setTimeSpent(timeRes.data.data);
    } catch (fetchError) {
      console.error('Error fetching analytics:', fetchError);
      setError(formatError(fetchError, t('failedLoad')));
    } finally {
      setLoading(false);
    }
  }, [queryParams, t]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const applyPreset = (days, presetId) => {
    const range = getPresetRange(days);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setPreset(presetId);
  };

  const overdueChartData = tasksTrend
    ? [
        { name: t('overdueOnTime'), value: tasksTrend.overdueVsOnTime.onTime },
        { name: t('overdueOverdue'), value: tasksTrend.overdueVsOnTime.overdue },
        { name: t('overdueNoSchedule'), value: tasksTrend.overdueVsOnTime.noSchedule }
      ].filter((item) => item.value > 0)
    : [];

  const statusChartData = (tasksTrend?.byStatus || []).map((row) => ({
    name: formatTaskStatus(row.status, t),
    count: row.count
  }));

  const locationChartData = (tasksTrend?.byLocation || []).slice(0, 8);

  const completionsChartData = (tasksTrend?.completionsOverTime || []).map((row) => ({
    period: formatPeriodLabel(row.period),
    count: row.count
  }));

  const timeByPeriodData = (timeSpent?.totalByPeriod || []).map((row) => ({
    period: formatPeriodLabel(row.period),
    minutes: row.minutes
  }));

  const userCompletionsData = (userPerformance?.users || []).slice(0, 8).map((row) => ({
    name: row.userName,
    completions: row.completions,
    onTimeRate: row.onTimeRate != null ? Math.round(row.onTimeRate * 100) : 0
  }));

  const actualVsEstimatedData = (timeSpent?.actualVsEstimated || []).slice(0, 8);

  return (
    <div className="analytics">
      <div className="container container--wide main-content">
        <div className="page-header analytics-header">
          <div>
            <h1>{t('pageTitle')}</h1>
            <p className="analytics-subtitle">
              {t('subtitle')}
            </p>
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="card analytics-filters">
          <div className="analytics-preset-row">
            {presetRanges.map((range) => (
              <button
                key={range.id}
                type="button"
                className={`btn btn-secondary btn-sm ${preset === range.id ? 'active' : ''}`}
                onClick={() => applyPreset(range.days, range.id)}
              >
                {range.label}
              </button>
            ))}
          </div>

          <div className="analytics-filter-row">
            <div>
              <label htmlFor="analytics-start">{t('startDateLabel')}</label>
              <input
                id="analytics-start"
                type="date"
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  setPreset('custom');
                }}
              />
            </div>
            <div>
              <label htmlFor="analytics-end">{t('endDateLabel')}</label>
              <input
                id="analytics-end"
                type="date"
                value={endDate}
                onChange={(event) => {
                  setEndDate(event.target.value);
                  setPreset('custom');
                }}
              />
            </div>
            <div>
              <label htmlFor="analytics-bucket">{t('groupByLabel')}</label>
              <select
                id="analytics-bucket"
                value={bucket}
                onChange={(event) => setBucket(event.target.value)}
              >
                <option value="day">{t('bucketDay')}</option>
                <option value="week">{t('bucketWeek')}</option>
              </select>
            </div>
            {isAdmin && (
              <div>
                <label htmlFor="analytics-user">{t('userLabel')}</label>
                <select
                  id="analytics-user"
                  value={filterUserId}
                  onChange={(event) => setFilterUserId(event.target.value)}
                >
                  <option value="">{t('allUsers')}</option>
                  {companyUsers.map((companyUser) => (
                    <option key={companyUser.id} value={companyUser.id}>
                      {formatUserLabel(companyUser)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {isAdmin && (
              <div>
                <label htmlFor="analytics-location">{t('locationLabel')}</label>
                <select
                  id="analytics-location"
                  value={filterLocationId}
                  onChange={(event) => setFilterLocationId(event.target.value)}
                >
                  <option value="">{t('allLocations')}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="analytics-priority">{t('priorityLabel')}</label>
              <select
                id="analytics-priority"
                value={filterPriority}
                onChange={(event) => setFilterPriority(event.target.value)}
              >
                <option value="">{t('allPriorities')}</option>
                <option value="low">{formatTaskPriority('low', t)}</option>
                <option value="medium">{formatTaskPriority('medium', t)}</option>
                <option value="high">{formatTaskPriority('high', t)}</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner className="analytics-loading" />
        ) : (
          <>
            {summary && (
              <section className="analytics-summary-grid">
                <div className="card analytics-stat-card">
                  <span className="analytics-stat-label">{t('statCompletedTasks')}</span>
                  <strong className="analytics-stat-value">{summary.totalCompleted}</strong>
                </div>
                <div className="card analytics-stat-card">
                  <span className="analytics-stat-label">{t('statOnTimeCompletions')}</span>
                  <strong className="analytics-stat-value">{summary.onTimeCompletions}</strong>
                </div>
                <div className="card analytics-stat-card">
                  <span className="analytics-stat-label">{t('statOverdueCompletions')}</span>
                  <strong className="analytics-stat-value">{summary.overdueCompletions}</strong>
                </div>
                <div className="card analytics-stat-card">
                  <span className="analytics-stat-label">{t('statTotalTimeSpent')}</span>
                  <strong className="analytics-stat-value">{formatMinutes(summary.totalTimeSpentMinutes)}</strong>
                </div>
              </section>
            )}

            <section className="analytics-section">
              <h2>{t('sectionTaskVolume')}</h2>
              <div className="analytics-charts-grid">
                <div className="card analytics-chart-card">
                  <h3>{t('chartCompletionsOverTime')}</h3>
                  {completionsChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <BarChart data={completionsChartData} margin={isNarrow ? { left: 0, right: 4 } : undefined}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                        <XAxis
                          dataKey="period"
                          tick={mutedTick}
                          interval={isNarrow ? 'preserveStartEnd' : 0}
                          angle={isNarrow ? -35 : 0}
                          textAnchor={isNarrow ? 'end' : 'middle'}
                          height={isNarrow ? 48 : undefined}
                        />
                        <YAxis allowDecimals={false} tick={mutedTick} width={isNarrow ? 28 : undefined} />
                        <Tooltip />
                        <Bar dataKey="count" fill="var(--color-primary)" name={t('seriesCompleted')} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyCompletionsOverTime')}
                    />
                  )}
                </div>

                <div className="card analytics-chart-card">
                  <h3>{t('chartTasksByStatus')}</h3>
                  {statusChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <BarChart data={statusChartData} layout="vertical" margin={isNarrow ? { left: 0, right: 8 } : undefined}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                        <XAxis type="number" allowDecimals={false} tick={mutedTick} />
                        <YAxis type="category" dataKey="name" width={categoryAxisWidth} tick={mutedTick} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#7B68EE" name={t('seriesTasks')} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyTasksByStatus')}
                    />
                  )}
                </div>

                <div className="card analytics-chart-card">
                  <h3>{t('chartCompletionsByLocation')}</h3>
                  {locationChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      {isNarrow ? (
                        <BarChart data={locationChartData} layout="vertical" margin={{ left: 0, right: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                          <XAxis type="number" allowDecimals={false} tick={mutedTick} />
                          <YAxis type="category" dataKey="location_name" width={categoryAxisWidth} tick={mutedTickSmall} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#48BB78" name={t('seriesCompleted')} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={locationChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                          <XAxis dataKey="location_name" tick={mutedTickSmall} interval={0} angle={-20} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} tick={mutedTick} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#48BB78" name={t('seriesCompleted')} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyCompletionsByLocation')}
                    />
                  )}
                </div>

                <div className="card analytics-chart-card">
                  <h3>{t('chartOnTimeVsOverdue')}</h3>
                  {overdueChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <PieChart>
                        <Pie
                          data={overdueChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={pieOuterRadius}
                          label={isNarrow ? false : ({ name, value }) => t('pieLabel', { name, value })}
                        >
                          {overdueChartData.map((entry, index) => (
                            <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend {...legendProps} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyOnTimeVsOverdue')}
                    />
                  )}
                </div>
              </div>
            </section>

            <section className="analytics-section">
              <h2>{t('sectionUserEfficiency')}</h2>
              <div className="analytics-charts-grid">
                <div className="card analytics-chart-card analytics-chart-wide">
                  <h3>{t('chartCompletionsPerUser')}</h3>
                  {userCompletionsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeightWide}>
                      {isNarrow ? (
                        <BarChart data={userCompletionsData} layout="vertical" margin={{ left: 0, right: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                          <XAxis type="number" allowDecimals={false} tick={mutedTick} />
                          <YAxis type="category" dataKey="name" width={categoryAxisWidth} tick={mutedTickSmall} />
                          <Tooltip />
                          <Legend {...legendProps} />
                          <Bar dataKey="completions" fill="var(--color-primary)" name={t('seriesCompletions')} radius={[0, 4, 4, 0]} />
                          <Bar dataKey="onTimeRate" fill="#4299E1" name={t('seriesOnTimePercent')} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={userCompletionsData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                          <XAxis dataKey="name" tick={mutedTickSmall} interval={0} angle={-15} textAnchor="end" height={70} />
                          <YAxis allowDecimals={false} tick={mutedTick} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="completions" fill="var(--color-primary)" name={t('seriesCompletions')} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="onTimeRate" fill="#4299E1" name={t('seriesOnTimePercent')} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyUserPerformance')}
                    />
                  )}
                </div>

                <div className="card analytics-table-card analytics-chart-wide">
                  <h3>{t('tableEfficiencyBreakdown')}</h3>
                  {(userPerformance?.users || []).length > 0 ? (
                    <div className="table-responsive">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>{t('columnUser')}</th>
                            <th>{t('columnCompletions')}</th>
                            <th>{t('columnOnTimeRate')}</th>
                            <th>{t('columnAvgVariance')}</th>
                            <th>{t('columnStartLatency')}</th>
                            <th>{t('columnScore')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userPerformance.users.map((row) => (
                            <tr key={row.userId}>
                              <td>{row.userName}</td>
                              <td>{row.completions}</td>
                              <td>{formatPercent(row.onTimeRate)}</td>
                              <td>
                                {row.avgVarianceMinutes != null
                                  ? t('varianceMinutes', {
                                    sign: row.avgVarianceMinutes > 0 ? '+' : '',
                                    minutes: row.avgVarianceMinutes
                                  })
                                  : t('emptyValue')}
                              </td>
                              <td>
                                {row.avgAssignmentToStartMinutes != null
                                  ? formatMinutes(row.avgAssignmentToStartMinutes)
                                  : t('emptyValue')}
                              </td>
                              <td>{row.efficiencyScore}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyEfficiencyBreakdown')}
                    />
                  )}
                </div>
              </div>
            </section>

            <section className="analytics-section">
              <h2>{t('sectionTimeSpent')}</h2>
              <div className="analytics-charts-grid">
                <div className="card analytics-chart-card">
                  <h3>{t('chartTotalTimeByPeriod')}</h3>
                  {timeByPeriodData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <LineChart data={timeByPeriodData} margin={isNarrow ? { left: 0, right: 4 } : undefined}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                        <XAxis
                          dataKey="period"
                          tick={mutedTick}
                          interval={isNarrow ? 'preserveStartEnd' : 0}
                          angle={isNarrow ? -35 : 0}
                          textAnchor={isNarrow ? 'end' : 'middle'}
                          height={isNarrow ? 48 : undefined}
                        />
                        <YAxis tick={mutedTick} width={isNarrow ? 36 : undefined} />
                        <Tooltip formatter={(value) => formatMinutes(value)} />
                        <Line type="monotone" dataKey="minutes" stroke="var(--color-primary)" strokeWidth={2} name={t('seriesMinutes')} dot={{ r: isNarrow ? 2 : 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyTimeByPeriod')}
                    />
                  )}
                </div>

                <div className="card analytics-chart-card">
                  <h3>{t('chartActualVsEstimated')}</h3>
                  {actualVsEstimatedData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      {isNarrow ? (
                        <BarChart data={actualVsEstimatedData} layout="vertical" margin={{ left: 0, right: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                          <XAxis type="number" tick={mutedTick} />
                          <YAxis type="category" dataKey="userName" width={categoryAxisWidth} tick={mutedTickSmall} />
                          <Tooltip formatter={(value) => formatMinutes(value)} />
                          <Legend {...legendProps} />
                          <Bar dataKey="actualMinutes" fill="var(--color-primary)" name={t('seriesActual')} radius={[0, 4, 4, 0]} />
                          <Bar dataKey="estimatedMinutes" fill="#ED8936" name={t('seriesEstimated')} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={actualVsEstimatedData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-primary-fade)" />
                          <XAxis dataKey="userName" tick={mutedTickSmall} interval={0} angle={-15} textAnchor="end" height={70} />
                          <YAxis tick={mutedTick} />
                          <Tooltip formatter={(value) => formatMinutes(value)} />
                          <Legend />
                          <Bar dataKey="actualMinutes" fill="var(--color-primary)" name={t('seriesActual')} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="estimatedMinutes" fill="#ED8936" name={t('seriesEstimated')} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyActualVsEstimated')}
                    />
                  )}
                </div>

                <div className="card analytics-table-card analytics-chart-wide">
                  <h3>{t('tableTopTasksByTime')}</h3>
                  {(timeSpent?.topTasks || []).length > 0 ? (
                    <div className="table-responsive">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>{t('columnTask')}</th>
                            <th>{t('columnTimeSpent')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timeSpent.topTasks.map((row) => (
                            <tr key={row.taskId}>
                              <td>{row.title}</td>
                              <td>{formatMinutes(row.minutes)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <AnalyticsChartEmpty
                      title={t('emptyChartTitle')}
                      description={t('emptyTopTasksByTime')}
                    />
                  )}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
